<template>
  <div class="flex flex-col w-full gap-3 pb-6 overflow-y-auto h-full" dir="rtl">
    <button type="button" class="add-row" @click="add">
      <i class="pi pi-plus text-sm" />
      משימה חדשה ל{{ firstName }}
    </button>

    <div v-if="loading" class="flex flex-col gap-3">
      <Skeleton v-for="i in 3" :key="i" height="4rem" border-radius="14px" />
    </div>

    <template v-else>
      <template v-if="open.length">
        <span class="section">פתוחות</span>
        <TaskRow
          v-for="task in open"
          :key="task.id"
          :task="task"
          hide-customer
          show-assignee
          :busy="busyId === task.id"
          @toggle="toggle"
          @open="edit"
        />
      </template>
      <template v-if="done.length">
        <span class="section mt-2">בוצעו</span>
        <TaskRow
          v-for="task in done"
          :key="task.id"
          :task="task"
          hide-customer
          :busy="busyId === task.id"
          @toggle="toggle"
          @open="edit"
        />
      </template>
      <div v-if="!open.length && !done.length" class="flex flex-col items-center text-center gap-2 pt-8">
        <i class="pi pi-check-square text-4xl text-surface-300" />
        <span class="text-sm text-surface-500">אין משימות ללקוח הזה</span>
        <span class="text-xs text-surface-400">למשל: לחזור אליו אחרי טיפול, לשמור לו מוצר, לבדוק איך הצבע</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { type ICustomer, type ITask, TaskStatusEnum } from '@torim-app/types';
import { useAdminTasksStore } from '@app/stores';
import { TasksApi } from '@app/api/admin/tasks-api';
import TaskRow from './TaskRow.vue';
import { useTaskEditor } from './use-task-editor';

const props = defineProps<{ customer: ICustomer }>();

const tasksStore = useAdminTasksStore();
const toast = useToast();
const { openTaskEditor } = useTaskEditor();

const tasks = ref<ITask[]>([]);
const loading = ref(true);
const busyId = ref<string>();

const firstName = computed(() => props.customer.fullName?.split(' ')[0] ?? '');
const open = computed(() => tasks.value.filter(t => t.status === TaskStatusEnum.Open));
const done = computed(() => tasks.value.filter(t => t.status === TaskStatusEnum.Done));

const load = async () => {
  try {
    tasks.value = await TasksApi.listForCustomer(props.customer.id);
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון את המשימות');
  } finally {
    loading.value = false;
  }
};

const toggle = async (task: ITask) => {
  if (!task.id || busyId.value) return;
  busyId.value = task.id;
  try {
    await tasksStore.setDone(task.id, task.status !== TaskStatusEnum.Done);
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את המשימה');
  } finally {
    busyId.value = undefined;
  }
};

const add = () => openTaskEditor({
  defaults: { customerId: props.customer.id, customerName: props.customer.fullName },
  lockCustomer: true,
});
const edit = (task: ITask) => openTaskEditor({ task, lockCustomer: true });

watch(() => tasksStore.version, load);
onMounted(load);
</script>

<style scoped lang="scss">
.add-row {
  @apply min-h-12 rounded-xl bg-white text-primary-700 text-[15px] font-semibold flex items-center justify-center gap-2;
  border: 1.5px dashed #b6a8e6;
}

.section {
  @apply text-[13px] font-bold text-surface-600 mt-1;
}
</style>
